import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environmnent } from '@environments/environment';
import { User } from '@shared/interfaces/user.interface';
import { JwtUtils } from '@shared/utils/jwt.utils';
import { BehaviorSubject, Observable, tap, throwError } from 'rxjs';
import { Role } from '@shared/enums/role.enum';
import { LoginResponse, UserSession } from '../models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'userToken';
  private readonly USER_KEY = 'userData';
  readonly loggedIn = new BehaviorSubject<boolean>(this.hasToken());
  readonly currentUser = new BehaviorSubject<UserSession | User | null>(this.getUserData());

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  readonly client_id: string = 'client_public_sales';
  readonly grant_type: string = 'password';
  readonly scope: string = 'grupomadero_sales_api offline_access';

  onRequestLogin(username: string, pass: string): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const payload = new HttpParams()
      .set('username', username)
      .set('password', pass)
      .set('client_id', this.client_id)
      .set('grant_type', this.grant_type)
      .set('scope', this.scope);

    return this.http.post<LoginResponse>(
      `${environmnent.AUTH_API}/connect/token`,
      payload.toString(),
      { headers }
    );
  }

  refreshToken(): Observable<LoginResponse> {
    const userData = this.getUserData();
    
    if (!userData?.refresh_token) {
      return throwError(() => new Error('No refresh token available'));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const payload = new HttpParams()
      .set('client_id', this.client_id)
      .set('grant_type', 'refresh_token')
      .set('refresh_token', userData.refresh_token)
      .set('scope', this.scope);

    return this.http.post<LoginResponse>(
      `${environmnent.AUTH_API}/connect/token`,
      payload.toString(),
      { headers }
    ).pipe(
      tap((response: LoginResponse) => {
        this.setToken(response.access_token);
        this.setUserData(response);
        this.loggedIn.next(true);
      })
    );
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.loggedIn.next(false);
    this.currentUser.next(null);
    this.router.navigate(['/login']);
  }

  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  setUserData(user: LoginResponse | User): void {
    const decodedToken = JwtUtils.decodeToken(user.access_token);
    const normalizedRole = this.normalizeRole(decodedToken?.role);
    const expirationTime = Date.now() + (user.expires_in * 1000);
    const customUser: UserSession = { 
      access_token: user.access_token,
      expires_in: user.expires_in,
      token_type: user.token_type,
      refresh_token: user.refresh_token,
      scope: user.scope,
      expirationTime, 
      role: normalizedRole 
    };
    localStorage.setItem(this.USER_KEY, JSON.stringify(customUser));
    this.currentUser.next(customUser);
  }

  getUserData(): UserSession | null {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      return userData ? (JSON.parse(userData) as UserSession) : null;
    } catch {
      return null;
    }
  }

  private normalizeRole(role: string | string[] | undefined): string | undefined {
    if (!role) {
      return undefined;
    }

    if (typeof role === 'string') {
      return role;
    }

    if (Array.isArray(role)) {
      const allowedRoles = Object.values(Role);
      const validRole = role.find(r => allowedRoles.includes(r as Role));
      return validRole;
    }

    return undefined;
  }

  isTokenExpiringSoon(minutesBeforeExpiry: number = 5): boolean {
    const userData = this.getUserData();
    if (!userData?.expirationTime) {
      return true;
    }

    const now = Date.now();
    const timeToExpiry = userData.expirationTime - now;
    const minutesInMs = minutesBeforeExpiry * 60 * 1000;

    return timeToExpiry <= minutesInMs;
  }

  hasToken(): boolean {
    const token = this.getToken();
    return !!token;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token || !this.getUserData()) {
      return false;
    }
    const decoded = JwtUtils.decodeToken(token);
    if (decoded?.exp) {
      return !JwtUtils.isTokenExpired(token);
    }
    return true;
  }

  hasRole(role: string): boolean {
    const token = this.getToken();
    if (token) {
      const decoded = JwtUtils.decodeToken(token);
      if (decoded?.role) {
        return this.normalizeRole(decoded.role) === role;
      }
    }
    const user = this.getUserData();
    return user ? user.role === role : false;
  }

  isLoggedIn(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  getCurrentUser(): Observable<UserSession | User | null> {
    return this.currentUser.asObservable();
  }
}

